import React, { createContext, useContext, useMemo, useState } from 'react';

export const ICON_STYLE_STORAGE_KEY = 'prologue.icon-style';
export type IconStyle = 'hand-drawn' | 'standard-svg';

function isIconStyle(value: string | null): value is IconStyle {
  return value === 'hand-drawn' || value === 'standard-svg';
}

export function readStoredIconStyle(): IconStyle {
  if (typeof window === 'undefined') return 'hand-drawn';
  const stored = window.localStorage.getItem(ICON_STYLE_STORAGE_KEY);
  return isIconStyle(stored) ? stored : 'hand-drawn';
}

export interface IconStyleContextValue {
  iconStyle: IconStyle;
  setIconStyle: (style: IconStyle) => void;
}

const defaultContext: IconStyleContextValue = {
  iconStyle: 'hand-drawn',
  setIconStyle: () => undefined,
};

export const IconStyleContext = createContext<IconStyleContextValue>(defaultContext);

export const IconStyleProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [iconStyle, setIconStyleState] = useState<IconStyle>(readStoredIconStyle);

  const setIconStyle = (next: IconStyle): void => {
    setIconStyleState(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(ICON_STYLE_STORAGE_KEY, next);
    }
  };

  const value = useMemo(() => ({ iconStyle, setIconStyle }), [iconStyle]);
  return <IconStyleContext.Provider value={value}>{children}</IconStyleContext.Provider>;
};

export function useIconStyle(): IconStyleContextValue {
  return useContext(IconStyleContext);
}
